import React from 'react';
import { Menu, Bell, User, Shield, Moon, Sun } from 'lucide-react';
import { useAuth } from '@/core/hooks';
import { Button } from '@/core/components';
import { getPrimaryRole, getRoleLabel } from '@/core/utils/role-routing';
import { useProductUX } from './ProductUXProvider';
import { useCompanyProfile } from '@/app/utils/companyProfile';

export interface HeaderProps {
  onMenuClick: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { user } = useAuth();
  const { isDarkMode, language, setLanguage, toggleThemeMode, t } = useProductUX();
  const companyProfile = useCompanyProfile();

  const primaryRole = user ? getPrimaryRole(user.roles) : null;
  const roleLabel = primaryRole ? getRoleLabel(primaryRole) : 'User';

  const getRoleBadgeColor = (role: string | null) => {
    if (role === 'ADMIN') return 'bg-blue-100 text-blue-700';
    if (role === 'COORDINATOR') return 'bg-cyan-100 text-cyan-700';
    if (role === 'MANAGER') return 'bg-amber-100 text-amber-700';
    if (role === 'CARER' || role === 'CAREGIVER') return 'bg-emerald-100 text-emerald-700';
    if (role === 'FAMILY') return 'bg-pink-100 text-pink-700';
    return 'bg-purple-100 text-purple-700';
  };

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/92 shadow-sm shadow-slate-950/5 backdrop-blur-xl dark:border-slate-800 dark:bg-slate-950/92">
      <div className="flex h-16 items-center justify-between gap-3 px-3 sm:px-5">
        <div className="flex min-w-0 items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onMenuClick}
            className="lg:hidden"
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </Button>
          <div className="hidden min-w-0 items-center gap-3 sm:flex">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-md bg-white shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800">
              <img src={companyProfile.logoMarkUrl} alt="" className="h-10 w-10 object-contain" />
            </div>
            <div className="min-w-0">
              <img src={companyProfile.logoFullUrl} alt={companyProfile.publicName} className="h-9 w-auto max-w-[230px] object-contain" />
              <p className="mt-0.5 truncate text-xs font-semibold text-slate-600 dark:text-slate-300">
                {t('header.product')}
              </p>
            </div>
          </div>
          <img src={companyProfile.logoFullUrl} alt={companyProfile.publicName} className="h-12 w-auto max-w-[170px] object-contain sm:hidden" />
        </div>

        <div className="flex min-w-0 items-center gap-2 sm:gap-4">
          <div
            className="flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 p-1 shadow-sm dark:border-slate-800 dark:bg-slate-900"
            aria-label="Application language"
          >
            <button
              type="button"
              onClick={() => setLanguage('en')}
              aria-label="Switch application to English"
              title="English"
              className={`grid h-8 w-9 place-items-center rounded-full text-lg transition-all ${
                language === 'en'
                  ? 'bg-white shadow-sm ring-2 ring-purple-500 dark:bg-slate-800 dark:ring-fuchsia-300'
                  : 'opacity-70 hover:bg-white hover:opacity-100 dark:hover:bg-slate-800'
              }`}
            >
              <img src="/images/flag-uk.png" alt="English" className="h-5 w-7 rounded-sm object-cover" />
            </button>
            <button
              type="button"
              onClick={() => setLanguage('ro')}
              aria-label="Switch application to Romanian"
              title="Română"
              className={`grid h-8 w-9 place-items-center rounded-full text-lg transition-all ${
                language === 'ro'
                  ? 'bg-white shadow-sm ring-2 ring-purple-500 dark:bg-slate-800 dark:ring-fuchsia-300'
                  : 'opacity-70 hover:bg-white hover:opacity-100 dark:hover:bg-slate-800'
              }`}
            >
              <img src="/images/flag-ro.jpg" alt="Română" className="h-6 w-6 rounded-full object-cover" />
            </button>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleThemeMode}
            aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            title={isDarkMode ? 'Light mode' : 'Dark mode'}
          >
            {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
          <Button variant="ghost" size="sm" aria-label="Notifications">
            <Bell className="h-5 w-5" />
          </Button>
          <div className="flex min-w-0 items-center gap-3 rounded-full border border-slate-200 bg-slate-50 px-2 py-1 dark:border-slate-800 dark:bg-slate-900">
            <div className="hidden text-right sm:block">
              <p className="truncate text-sm font-medium text-gray-900 dark:text-slate-100">{user?.name}</p>
              <div className="flex items-center gap-1 justify-end">
                <Shield className="h-3 w-3 text-gray-400" />
                <span
                  className={`text-xs font-medium px-2 py-0.5 rounded-full ${getRoleBadgeColor(
                    primaryRole
                  )}`}
                >
                  {roleLabel}
                </span>
              </div>
            </div>
            <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center text-white">
              <User className="h-5 w-5" />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
